import { useEffect, useRef, useState } from "react";
import FilterPanel from "./filter-panel/FilterPanel";
import { Box, useTheme } from "@mui/material";
import GlobalSpinner from "components/common/components/Spinner/GlobalSpinner";
import { useLookupData } from "../common/hooks/useLookupData";
import StatusBoard from "./status-board/StatusBoard";
import TechBoard from "./tech-board/TechBoard";
import PriorityBoard from "./priority-board/PriorityBoard";
import { BoardView } from "./types/boardView";
import { VIEW } from "components/common/constants/filterParamConstants";
import { useLocation } from "react-router-dom";
import { track } from "@amplitude/analytics-browser";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import {
  DELIVERY_FROM_DATE,
  DELIVERY_TO_DATE,
  FILTER_CLOCKED_HOURS,
  FILTER_OVER_CLOCKED_HOURS,
  FROMDATE,
  GENERIC_SEARCH,
  INCLUDE_FINALIZED,
  PICKUP_FROM_DATE,
  PICKUP_TO_DATE,
  SCHEDULE_FROM_DATE,
  SCHEDULE_TO_DATE,
  SELECTED_WO_CUSTOMERS,
  SELECTED_WO_LOCS,
  SELECTED_WO_statuses,
  SELECTED_WO_TECHS,
  SELECTED_WO_SALES,
  TODATE,
  USER_IDENTITY,
} from "../common/constants/filterParamConstants";
import { Filter } from "./models/workOrderTypes";
// import GlobalSpinner from "components/common/components/Spinner/GlobalSpinner"; // Uncomment to use custom spinner

const MainContainer = () => {
  const theme = useTheme();
  useLookupData();
  //#region Amplitude
  const location = useLocation();
  useEffect(() => {
    track("Dashboard Viewed", {
      page: location.pathname,
      title: document.title,
    });
  }, [location]);
  //#endregion

  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [StatusBoardHeight, setStatusBoardHeight] = useState<number>(0);
  const [userName] = useState<string | "">(
    localStorage.getItem("username") || "",
  );

  const [currentView, setCurrentView] = useState<number>(() => {
    const storedView = localStorage.getItem(`${VIEW}_${userName}`);
    return storedView !== null ? parseInt(storedView, 10) : 0;
  });
  // Track last selected board view (0 = status, 1 = Tech)
  const LAST_BOARD_VIEW_KEY = `LAST_BOARD_VIEW_${userName}`;

  const { loading, setFilters } = useWorkOrderStore();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Load cached filters and trigger initial fetch on component mount
  useEffect(() => {
    const loadCachedFiltersAndFetch = () => {
      // Load cached filters from localStorage
      const cachedFilters: Partial<Filter> = {};

      // Load user identity
      const userIdentity = localStorage.getItem(USER_IDENTITY);
      if (userIdentity) {
        const parsedUserIdentity = parseInt(userIdentity, 10);
        if (!isNaN(parsedUserIdentity)) {
          cachedFilters.userIdentity = parsedUserIdentity;
        }
      }

      // Load generic search
      const genericSearch = localStorage.getItem(
        `${GENERIC_SEARCH}_${userName}`,
      );
      if (genericSearch) {
        cachedFilters.searchString = genericSearch;
      }

      // Load work order statuses
      const woStatuses = localStorage.getItem(
        `${SELECTED_WO_statuses}_${userName}`,
      );
      if (woStatuses && woStatuses !== "0") {
        cachedFilters.woStatuses = woStatuses.split(",").map((s) => s.trim());
      }

      // Load work order technicians
      const woTechs = localStorage.getItem(`${SELECTED_WO_TECHS}_${userName}`);
      if (woTechs && woTechs !== "0") {
        cachedFilters.woTechs = woTechs.split(",").map((s) => s.trim());
      }

      // Load sales persons
      const woSalesPersons = localStorage.getItem(
        `${SELECTED_WO_SALES}_${userName}`,
      );
      if (woSalesPersons && woSalesPersons !== "0") {
        cachedFilters.woSalesPersons = woSalesPersons
          .split(",")
          .map((s) => s.trim());
      }

      // Load work order locations
      const woLocs = localStorage.getItem(`${SELECTED_WO_LOCS}_${userName}`);
      if (woLocs && woLocs !== "0") {
        cachedFilters.woLocations = woLocs.split(",").map((s) => s.trim());
      }

      // Load customers
      const customers = localStorage.getItem(
        `${SELECTED_WO_CUSTOMERS}_${userName}`,
      );
      if (customers) {
        try {
          const parsedCustomers = JSON.parse(customers);
          if (Array.isArray(parsedCustomers) && parsedCustomers.length > 0) {
            cachedFilters.woCustomers = parsedCustomers.map((c) => c.identity);
          }
        } catch (error) {
          console.warn("Error parsing cached customers:", error);
        }
      }

      // Load date filters
      const getCachedDate = (key: string) => {
        const storedValue = localStorage.getItem(`${key}_${userName}`);
        if (storedValue) {
          try {
            const parsedValue = JSON.parse(storedValue);
            return parsedValue.from || parsedValue.to;
          } catch (error) {
            console.warn(`Error parsing cached date for ${key}:`, error);
          }
        }
        return null;
      };

      // Work order creation date
      const fromDate = getCachedDate(FROMDATE);
      const toDate = getCachedDate(TODATE);
      if (fromDate) cachedFilters.workOrderCreationStartDate = fromDate;
      if (toDate) cachedFilters.workOrderCreationEndDate = toDate;

      // Scheduled date
      const scheduleFromDate = getCachedDate(SCHEDULE_FROM_DATE);
      const scheduleToDate = getCachedDate(SCHEDULE_TO_DATE);
      if (scheduleFromDate)
        cachedFilters.workOrderScheduleFromDate = scheduleFromDate;
      if (scheduleToDate)
        cachedFilters.workOrderScheduleToDate = scheduleToDate;

      // Delivery date
      const deliveryFromDate = getCachedDate(DELIVERY_FROM_DATE);
      const deliveryToDate = getCachedDate(DELIVERY_TO_DATE);
      if (deliveryFromDate)
        cachedFilters.workOrderDeliveryStartDate = deliveryFromDate;
      if (deliveryToDate)
        cachedFilters.workOrderDeliveryEndDate = deliveryToDate;

      // Pickup date
      const pickupFromDate = getCachedDate(PICKUP_FROM_DATE);
      const pickupToDate = getCachedDate(PICKUP_TO_DATE);
      if (pickupFromDate)
        cachedFilters.workOrderPickupStartDate = pickupFromDate;
      if (pickupToDate) cachedFilters.workOrderPickupEndDate = pickupToDate;

      // Load boolean filters
      const clockedHours = localStorage.getItem(
        `${FILTER_CLOCKED_HOURS}_${userName}`,
      );
      if (clockedHours === "true") {
        cachedFilters.filterClockedHours = true;
      }

      const overClockedHours = localStorage.getItem(
        `${FILTER_OVER_CLOCKED_HOURS}_${userName}`,
      );
      if (overClockedHours === "true") {
        cachedFilters.filterOverClockedHours = true;
      }

      const includeFinalizedStored = localStorage.getItem(
        `${INCLUDE_FINALIZED}_${userName}`,
      );
      if (includeFinalizedStored === "true") {
        cachedFilters.includeFinalized = true;
      }

      // Set view
      cachedFilters.view = currentView;

      // Apply all cached filters to the store
      Object.entries(cachedFilters).forEach(([key, value]) => {
        setFilters(key, value);
      });

      // console.log("MainContainer: Applied cached filters:", cachedFilters);
      // console.log("MainContainer: Setting initialLoadComplete = true");

      // Don't trigger fetch here - let FilterPanel handle it
      // The FilterPanel's useEffect will trigger the fetch when filters change

      // Mark initial load as complete
      setInitialLoadComplete(true);
    };

    // Only run this effect once when the component mounts
    loadCachedFiltersAndFetch();
  }, []); // Empty dependency array ensures this runs only once on mount

  // No route-driven view switching here; logistics is a separate route now

  useEffect(() => {
    const calculateHeight = () => {
      if (filterPanelRef.current) {
        const filterHeight = filterPanelRef.current.offsetHeight;
        const windowHeight = window.innerHeight;
        setStatusBoardHeight(windowHeight - filterHeight - 67); // Adjust height dynamically
      }
    };

    // Create a ResizeObserver to track FilterPanel height changes
    const resizeObserver = new ResizeObserver(() => {
      calculateHeight(); // Recalculate height when FilterPanel changes size
    });

    // Observe the FilterPanel
    if (filterPanelRef.current) {
      resizeObserver.observe(filterPanelRef.current);
    }

    // Cleanup observer on unmount
    return () => {
      resizeObserver.disconnect();
      //That change was done before holidays
    };
  }, []);
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        height: "100vh", // Full viewport height
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        ref={filterPanelRef}
        sx={{
          flexShrink: 0, // Prevent resizing
        }}
      >
        <FilterPanel
          selectedView={currentView}
          onSelectedViewChange={(view: number) => {
            localStorage.setItem(`${VIEW}_${userName}`, view.toString());
            setCurrentView(view);
            setFilters("view", view);
            if (
              view === BoardView.Status ||
              view === BoardView.Technician ||
              view === BoardView.Priority
            ) {
              localStorage.setItem(LAST_BOARD_VIEW_KEY, view.toString());
            }
          }}
          initialLoadComplete={initialLoadComplete}
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflow: "hidden", // Prevent any overflow
        }}
      >
        <Box
          sx={{
            // overflowX: "auto", // Enable horizontal scroll
            height: `${StatusBoardHeight}px`, // Use dynamically calculated height
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {loading ? (
            <GlobalSpinner />
          ) : (
            <Box sx={{ flexGrow: 1, minHeight: 0, width: "100%" }}>
              {currentView === BoardView.Status && <StatusBoard />}
              {currentView === BoardView.Technician && <TechBoard />}
              {currentView === BoardView.Priority && <PriorityBoard />}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MainContainer;
